import React from 'react';
import { isNil } from 'lodash';
import OpenPreviewProductDetail from '../../../../../containers/app/common/OpenPreviewProductDetail';
import OpenOrderDeliveryTracking from '../../../../../containers/app/common/OpenOrderDeliveryTracking';
import { AMONDZ_MEMBER_TYPE, DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import TableFilterInput from '../../../../../components/table/TableFilterInput';
import OpenBrandDetail from '../../../../../components/link/OpenBrandDetail';
import OpenProductDetail from '../../../../../components/link/OpenProductDetail';
import OpenOrderItemDetail from '../../../../../components/link/OpenOrderItemDetail';
import OpenUserDetail from '../../../../../components/link/OpenUserDetail';
import Select from '../../../../../components/input/Select';
import {
  ORDER_DELIVERY_TYPE,
  ORDER_DELIVERY_TYPE_TEXT,
  ORDER_TYPE_SELECT_LIST,
  ORDER_GIFT_STATUS_SELECT_LIST,
  ORDER_ITEM_STATUS,
  ORDER_ITEM_STATUS_TEXT,
  PAYMENT_METHOD_TYPE_SELECT_LIST,
  PAY_METHOD_TYPE_TEXT,
  ORDER_GIFT_STATUS_TEXT,
  ORDER_TYPE_TEXT,
} from '../../../../../constants/service/app/orderConstants';
import {
  addAllItemToSelectList,
  setClearDash,
  setColumnWidth,
  setDash,
  setPhoneFormat,
  setNumberWithComma,
} from '../../../../../lib/common';
import { setDateFormat } from '../../../../../lib/moment';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import SendMessageService from '../../../../../containers/app/common/SendMessageService';
import { setOrderMessage } from '../../../../../lib/utils/orderutils';
import OpenCouponDetail from '../../../../../components/link/OpenCouponDetail';

/**
 *  주문 전체 리스트 컬럼
 */
const columns = (data) => {
  const { list, filterOption, onFilter, mainProductCategorySelectList } = data;

  return [
    {
      Header: '필수 정보',
      fixed: 'left',
      columns: [
        {
          accessor: 'orderId',
          Header: '주문번호',
          filterable: true,
          width: 150,
          Filter: () => (
            <TableFilterInput option={filterOption.orderId} onFilter={(orderId) => onFilter({ orderId })} />
          ),
        },
        {
          accessor: 'orderItemId',
          Header: '상품 주문번호',
          filterable: true,
          width: 150,
          Cell: (row) => <OpenOrderItemDetail text={row.value} orderItemId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.orderItemId} onFilter={(orderItemId) => onFilter({ orderItemId })} />
          ),
        },
        {
          accessor: 'orderItemStatus',
          Header: '주문 상태',
          width: 120,
          Cell: (row) => ORDER_ITEM_STATUS_TEXT[row.value] || '-',
        },
      ],
    },
    {
      accessor: 'orderType',
      Header: '주문 타입',
      filterable: true,
      width: setColumnWidth(list, 'orderType'),
      Cell: (row) => setDash(ORDER_TYPE_TEXT[row.value]),
      Filter: () => (
        <Select
          isTable
          list={addAllItemToSelectList(ORDER_TYPE_SELECT_LIST)}
          value={filterOption.orderType}
          onChange={(orderType) => onFilter({ orderType })}
        />
      ),
    },
    {
      accessor: 'giftStatus',
      Header: '선물 상태',
      filterable: true,
      width: 150,
      Cell: (row) => setDash(ORDER_GIFT_STATUS_TEXT[row.value]),
      Filter: () => (
        <Select
          isTable
          list={addAllItemToSelectList(ORDER_GIFT_STATUS_SELECT_LIST)}
          value={filterOption.giftStatus}
          onChange={(giftStatus) => onFilter({ giftStatus })}
        />
      ),
    },
    {
      Header: '주문 정보',
      columns: [
        {
          accessor: 'totalOrderPrice',
          Header: '주문별 총 결제금액',
          width: 130,
          Cell: (row) => (!isNil(row.value) ? setNumberWithComma(row.value) : ''),
        },
        {
          accessor: 'totalPrice',
          Header: '실판매가',
          width: 130,
          sortable: true,
          defaultSortDesc: true,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'brandName',
          Header: '브랜드명',
          resizable: true,
          filterable: true,
          width: setColumnWidth(list, 'brandName'),
          Cell: (row) => <OpenBrandDetail text={row.value} storeId={row.original.storeId} />,
          Filter: () => (
            <TableFilterInput option={filterOption.brandName} onFilter={(brandName) => onFilter({ brandName })} />
          ),
        },
        {
          accessor: 'storeId',
          Header: '브랜드 ID',
          resizable: true,
          filterable: true,
          width: 70,
          Cell: (row) => <OpenBrandDetail text={row.value} storeId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.storeIdList} onFilter={(storeIdList) => onFilter({ storeIdList })} />
          ),
        },
        {
          accessor: 'productName',
          Header: '상품명',
          className: 'align-left',
          resizable: true,
          filterable: true,
          width: setColumnWidth(list, 'productName'),
          Cell: (row) => <OpenPreviewProductDetail text={row.value} productId={row.original.productId} />,
          Filter: () => (
            <TableFilterInput option={filterOption.productName} onFilter={(productName) => onFilter({ productName })} />
          ),
        },
        {
          accessor: 'productNumber',
          Header: '제품 번호',
          className: 'align-left',
          resizable: true,
          filterable: true,
          width: 100,
          Filter: () => (
            <TableFilterInput
              option={filterOption.productNumber}
              onFilter={(productNumber) => onFilter({ productNumber })}
            />
          ),
        },
        {
          accessor: 'mainCategory',
          Header: '1차 카테고리',
          width: setColumnWidth(list, 'mainCategory') + 40,
          filterable: true,
          Filter: () => (
            <Select
              list={addAllItemToSelectList(mainProductCategorySelectList)}
              onChange={(mainCategory) =>
                onFilter({
                  mainCategory,
                  subCategory: null,
                })
              }
              defaultValue={filterOption.mainCategory}
              value={filterOption.mainCategory}
              isTable={true}
              size="large"
            />
          ),
        },
        {
          accessor: 'buyerName',
          Header: '구매자명',
          filterable: true,
          width: setColumnWidth(list, 'buyerName'),
          Filter: () => (
            <TableFilterInput option={filterOption.buyerName} onFilter={(buyerName) => onFilter({ buyerName })} />
          ),
        },
        {
          accessor: 'buyerPhone',
          Header: '구매자 연락처',
          filterable: true,
          width: 170,
          Cell: (row) => (
            <SendMessageService
              text={setPhoneFormat(row.value)}
              memberType={AMONDZ_MEMBER_TYPE.USER}
              targetId={row.original.userId}
            />
          ),
          Filter: () => (
            <TableFilterInput
              option={filterOption.buyerPhone}
              onFilter={(buyerPhone) =>
                onFilter({
                  buyerPhone: setClearDash(buyerPhone),
                })
              }
            />
          ),
        },
        {
          accessor: 'orderMessage',
          Header: '주문 메시지',
          Cell: (row) => setOrderMessage(row.value),
        },
      ],
    },
    {
      Header: '거래 정보',
      columns: [
        {
          accessor: 'productId',
          Header: '상품 ID',
          filterable: true,
          Cell: (row) => <OpenProductDetail text={row.value} productId={row.value} />,
          Filter: () => (
            <TableFilterInput option={filterOption.productId} onFilter={(productId) => onFilter({ productId })} />
          ),
        },
        {
          accessor: 'productOptionName',
          Header: '옵션정보',
          resizable: true,
          width: setColumnWidth(list, 'productOptionName'),
          Cell: (row) => setDash(row.value),
        },
        {
          accessor: 'productOptionNumber',
          Header: '옵션 제품 번호',
          className: 'align-left',
          resizable: true,
          filterable: true,
          width: 100,
          Cell: (row) => setDash(row.value),
          Filter: () => (
            <TableFilterInput
              option={filterOption.productOptionNumber}
              onFilter={(productOptionNumber) => onFilter({ productOptionNumber })}
            />
          ),
        },
        {
          accessor: 'quantity',
          Header: '수량',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productOriginalPrice',
          Header: '상품 가격',
          width: 130,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productDiscountPrice',
          Header: '할인액',
          width: 130,
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'productOptionPrice',
          Header: '옵션 가격',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'deliveryExtraFee',
          Header: '추가 배송비',
          Cell: (row) => setNumberWithComma(row.value),
        },
        {
          accessor: 'usedCoupon',
          Header: '쿠폰',
          Cell: (row) =>
            row.original.couponId ? (
              <OpenCouponDetail text={setNumberWithComma(row.value)} couponId={row.original.couponId} />
            ) : (
              row.value
            ),
        },
        {
          accessor: 'usedPoint',
          Header: '포인트',
          Cell: (row) => setNumberWithComma(row.value),
        },
      ],
    },
    {
      Header: '배송 정보',
      columns: [
        {
          accessor: 'orderDate',
          Header: '주문 일시',
          sortable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
        },
        {
          accessor: 'paymentDate',
          Header: '결제 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.paymentDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ paymentDate: data })}
            />
          ),
        },
        {
          accessor: 'deliveryPrepareDate',
          Header: '상품 준비 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.deliveryPrepareDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ deliveryPrepareDate: data })}
            />
          ),
        },
        {
          accessor: 'deliveryDispatchDate',
          Header: '발송 처리 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.deliveryDispatchDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ deliveryDispatchDate: data })}
            />
          ),
        },
        {
          Header: '배송 추적',
          Cell: (row) => {
            /**
             *  상품 주문 상태가 배송 중/배송 완료이며, 택배방법이 [택배, 등기, 소포]일 경우에만 추적 활성화
             */
            if (
              (row.original.orderItemStatus === ORDER_ITEM_STATUS.SHIPPING ||
                row.original.orderItemStatus === ORDER_ITEM_STATUS.DELIVERY_COMPLETE) &&
              row.original.deliveryType === ORDER_DELIVERY_TYPE.PARCEL_DELIVERY
            )
              return <OpenOrderDeliveryTracking deliveryId={row.original.deliveryId} />;
            else return setDash(row.value);
          },
        },
        {
          accessor: 'deliveryType',
          Header: '배송 방법',
          Cell: (row) => ORDER_DELIVERY_TYPE_TEXT[row.value] || '-',
        },
        {
          accessor: 'deliveryEnterprise',
          Header: '택배사',
          Cell: (row) => setDash(row.value),
        },
        {
          accessor: 'invoiceNumber',
          Header: '송장 번호',
          filterable: true,
          Cell: (row) => setDash(row.value),
          Filter: () => (
            <TableFilterInput
              option={filterOption.invoiceNumber}
              onFilter={(invoiceNumber) => onFilter({ invoiceNumber })}
            />
          ),
        },
        {
          accessor: 'deliveryCompleteDate',
          Header: '배송 완료 일시',
          sortable: true,
          filterable: true,
          width: 320,
          Cell: (row) => setDateFormat(row.value, DATE_FORMAT.YMD_HM_DASH_FORMAT),
          Filter: () => (
            <DateRangePicker
              value={filterOption.deliveryCompleteDate}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              onSelect={(data) => onFilter({ deliveryCompleteDate: data })}
            />
          ),
        },
      ],
    },
    {
      Header: '이용자 정보',
      columns: [
        {
          accessor: 'userId',
          Header: '유저 식별 ID',
          filterable: true,
          width: setColumnWidth(list, 'userId'),
          Cell: (row) => <OpenUserDetail text={row.value} userId={row.value} />,
          Filter: () => <TableFilterInput option={filterOption.userId} onFilter={(userId) => onFilter({ userId })} />,
        },
        {
          accessor: 'buyerAccount',
          Header: '구매자 ID',
          filterable: true,
          width: setColumnWidth(list, 'buyerAccount'),
          Cell: (row) => <OpenUserDetail text={row.value} userId={row.original.userId} />,
          Filter: () => (
            <TableFilterInput
              option={filterOption.buyerAccount}
              onFilter={(buyerAccount) => onFilter({ buyerAccount })}
            />
          ),
        },
        {
          accessor: 'recipientName',
          Header: '수취인명',
          filterable: true,
          width: setColumnWidth(list, 'recipientName'),
          Filter: () => (
            <TableFilterInput
              option={filterOption.recipientName}
              onFilter={(recipientName) => onFilter({ recipientName })}
            />
          ),
        },
        {
          accessor: 'recipientPhone',
          Header: '수취인 연락처',
          filterable: true,
          width: 170,
          Cell: (row) => (
            <SendMessageService
              text={setPhoneFormat(row.value)}
              memberType={AMONDZ_MEMBER_TYPE.RECIPIENT}
              targetId={row.original.orderItemId}
            />
          ),
          Filter: () => (
            <TableFilterInput
              option={filterOption.recipientPhone}
              onFilter={(recipientPhone) =>
                onFilter({
                  recipientPhone: setClearDash(recipientPhone),
                })
              }
            />
          ),
        },
        {
          accessor: 'address',
          Header: '배송지',
          resizable: true,
          width: setColumnWidth(list, 'address'),
        },
        {
          accessor: 'postCode',
          Header: '우편번호',
        },
        {
          accessor: 'message',
          Header: '배송 메시지',
          className: 'align-left',
          resizable: true,
          width: 200,
        },
        {
          accessor: 'payMethod',
          Header: '결제수단',
          filterable: true,
          width: 170,
          Cell: (row) => PAY_METHOD_TYPE_TEXT[row.value] || '-',
          Filter: () => (
            <Select
              list={PAYMENT_METHOD_TYPE_SELECT_LIST}
              onChange={(payMethod) => onFilter({ payMethod })}
              defaultValue={filterOption.payMethod}
              value={filterOption.payMethod}
              isTable={true}
              size="large"
            />
          ),
        },
      ],
    },
    {
      Header: '수수료 정보',
      columns: [
        {
          accessor: 'isPgFee',
          Header: 'PG수수료',
          width: 120,
          Cell: (row) => (row.value ? 'O' : 'X'),
        },
        {
          accessor: 'amondzFeeRate',
          Header: '아몬즈수수료',
          width: 120,
          Cell: (row) => `${row.value}%`,
        },
        {
          accessor: 'marketingFeeRate',
          Header: '마케팅수수료',
          width: 120,
          Cell: (row) => `${row.value}%`,
        },
        {
          accessor: 'fulfillmentFeeRate',
          Header: '풀필먼트수수료',
          width: 120,
          Cell: (row) => `${row.value}%`,
        },
        {
          accessor: 'amondzLabFeeRate',
          Header: '아몬즈랩수수료',
          width: 120,
          Cell: (row) => `${row.value}%`,
        },
      ],
    },
  ];
};

export default columns;
